import { useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { FilterRowFlights, type FlightSortBy } from '@/components/search/FilterRow';
import { MatchMyTasteToggle } from '@/components/search/MatchMyTasteToggle';
import { useAuth } from '@/lib/auth';
import { ExpandableFlightCard } from '@/components/travel/expandable-flight-card';
import { SubscribeToBookInterstitial } from '@/components/search/SubscribeToBookInterstitial';
import { PricingLegend } from '@/components/search/PricingLegend';
import type { FlightResult, Flight, MultiSliceFlight } from '@/types/travel';

// Mirrors the helper inside FilterRow / the deleted filter-bar.tsx so
// the sort-by-duration path doesn't have to import a private helper.
const parseDurationToMinutes = (duration: string): number => {
  const h = duration.match(/(\d+)h/);
  const m = duration.match(/(\d+)m/);
  return (h ? parseInt(h[1]) : 0) * 60 + (m ? parseInt(m[1]) : 0);
};
// TODO(VOY-489-followup): the multi-slice aggregation here is SUM
// (total trip time across slices), but the filter predicate inside
// FilterRowFlights uses MAX (longest individual slice). That's a
// pre-existing inconsistency carried over verbatim from the deleted
// filter-bar.tsx. Decide which is right and align both call sites.
const getFlightDurationMinutes = (flight: FlightResult): number => {
  if ('type' in flight && flight.type === 'multi-slice') {
    return (flight as MultiSliceFlight).slices.reduce(
      (sum, s) => sum + parseDurationToMinutes(s.duration), 0);
  }
  return parseDurationToMinutes((flight as Flight).duration);
};
const getFlightPrice = (flight: FlightResult): number =>
  'totalPrice' in flight ? flight.totalPrice : (flight as Flight).price;
const getFlightDepartureMs = (flight: FlightResult): number => {
  if ('type' in flight && flight.type === 'multi-slice') {
    return new Date((flight as MultiSliceFlight).slices[0].departureTime).getTime();
  }
  return new Date((flight as Flight).departureTime).getTime();
};

interface FlightTabContentProps {
  flights: FlightResult[];
  itinerary?: any;
  isLoading?: boolean;
  /**
   * VOY-475: zero-based index AFTER which to render the
   * SubscribeToBookInterstitial. Defaults to 1 (= shown after the 2nd
   * flight, matching the pre-VOY-475 SubscribeCTABanner placement). The
   * interstitial only renders when there are more flights after that
   * index AND the user is non-subscribed (gating lives inside the
   * interstitial component).
   */
  interstitialAfterIndex?: number;
  /**
   * VOY-461: flight "Match my taste" toggle (parity with hotels). When
   * `onPersonalizeChange` is provided, the toggle renders next to the
   * filter row and drives the pipeline's personalizeFlights flag.
   */
  personalizeEnabled?: boolean;
  onPersonalizeChange?: (next: boolean) => void;
}

export function FlightTabContent({
  flights,
  itinerary,
  isLoading = false,
  interstitialAfterIndex = 1,
  personalizeEnabled = false,
  onPersonalizeChange,
}: FlightTabContentProps) {
  const { user } = useAuth();
  // VOY-475: subscription gating moved inside SubscribeToBookInterstitial,
  // so this component no longer needs to read rules itself.
  const [filteredFlights, setFilteredFlights] = useState<FlightResult[]>(flights);
  const [expandedFlightId, setExpandedFlightId] = useState<string | null>(null);
  // VOY-489: sort lives in this parent (the filter row triggers
  // setSortBy, the parent applies it to the filtered list before
  // rendering). Default 'best' = identity order (backend ranking).
  const [sortBy, setSortBy] = useState<FlightSortBy>('best');

  const hasActiveFilters = filteredFlights.length !== flights.length;

  // Apply sort to the filtered list. 'best' falls through to the
  // backend's original ranking; the others are local sorts.
  const sortedFlights = useMemo(() => {
    if (sortBy === 'best') return filteredFlights;
    const copy = [...filteredFlights];
    switch (sortBy) {
      case 'price-asc':
        return copy.sort((a, b) => getFlightPrice(a) - getFlightPrice(b));
      case 'price-desc':
        return copy.sort((a, b) => getFlightPrice(b) - getFlightPrice(a));
      case 'duration':
        return copy.sort((a, b) => getFlightDurationMinutes(a) - getFlightDurationMinutes(b));
      case 'departure':
        return copy.sort((a, b) => getFlightDepartureMs(a) - getFlightDepartureMs(b));
      default:
        return copy;
    }
  }, [filteredFlights, sortBy]);

  if (isLoading) {
    return (
      <div className="max-w-5xl mx-auto space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white border border-gray-200 rounded-xl overflow-hidden animate-pulse">
            <div className="p-4 space-y-3">
              <div className="flex justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 bg-gray-200 rounded-full" />
                  <div className="h-5 bg-gray-200 rounded w-32" />
                </div>
                <div className="h-6 bg-gray-200 rounded w-24" />
              </div>
              <div className="flex justify-between items-center pt-2">
                <div className="space-y-1">
                  <div className="h-4 bg-gray-200 rounded w-16" />
                  <div className="h-6 bg-gray-200 rounded w-20" />
                </div>
                <div className="h-px bg-gray-200 flex-1 mx-8" />
                <div className="space-y-1 text-right">
                  <div className="h-4 bg-gray-200 rounded w-16 ml-auto" />
                  <div className="h-6 bg-gray-200 rounded w-20 ml-auto" />
                </div>
              </div>
            </div>
          </div>
        ))}
        <div className="text-center text-sm text-gray-500 mt-6 flex items-center justify-center gap-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500" />
          Searching for the best flights...
        </div>
      </div>
    );
  }

  if (flights.length === 0) {
    return (
      <Card className="border-gray-200">
        <CardContent className="p-8">
          <div className="text-center text-gray-500">
            No flights found. Try adjusting your search criteria.
          </div>
        </CardContent>
      </Card>
    );
  }

  // Build flight list with CTA injection
  const renderFlightList = () => {
    const items: React.ReactNode[] = [];
    const displayFlights = sortedFlights;

    displayFlights.forEach((flight, index) => {
      items.push(
        <ExpandableFlightCard
          key={flight.id}
          flight={flight}
          isExpanded={expandedFlightId === flight.id}
          onExpandChange={(expanded) => { setExpandedFlightId(expanded ? flight.id : null); }}
          itinerary={itinerary}
          onSelect={() => {}}
          // VOY-487: mark the first result as the featured "BEST MATCH
          // FOR YOU" card. VOY-489 review fix: also require sort=best
          // — when the user sorts by price/duration/departure, index 0
          // is no longer the algorithm's recommended pick, so the
          // "best match" framing would be misleading.
          featured={index === 0 && !hasActiveFilters && sortBy === 'best'}
        />
      );

      // VOY-475: shared mid-list CTA. SubscribeToBookInterstitial
      // self-gates on subscription status, so we only check position
      // (don't insert after the last flight; nothing to scroll past).
      if (
        index === interstitialAfterIndex &&
        displayFlights.length > interstitialAfterIndex + 1
      ) {
        items.push(<SubscribeToBookInterstitial key="subscribe-cta" />);
      }
    });

    return items;
  };

  return (
    <div className="space-y-4">
      {/* Header: count + persistent filter row (VOY-489 — pill strip
          replaces the prior collapsible panel). Filters and sort
          stay visible so users see the filter state at a glance. */}
      <div className="max-w-5xl mx-auto flex items-center justify-between">
        <span className="text-base font-medium text-gray-900">
          {flights.length} flight{flights.length !== 1 ? 's' : ''} found
          {hasActiveFilters && (
            <span className="ml-2 text-sm text-gray-500">
              · {flights.length - filteredFlights.length} hidden by filters
            </span>
          )}
        </span>
      </div>

      <div className="max-w-5xl mx-auto">
        {/* VOY-489 review fix: `key` forces FilterRowFlights to remount
            when a new search arrives, so internal filter state
            (priceRange / durationRange / selected airlines / selected
            airports / time ranges) doesn't survive across searches
            with different price + duration distributions. Without
            this, stored bounds become stale and `priceActive` /
            `durationActive` can render as "active" with no user
            action. */}
        {/* VOY-461: flight Match-my-taste toggle, parity with hotels. */}
        {onPersonalizeChange && (
          <div className="flex justify-end mb-2">
            <MatchMyTasteToggle
              enabled={personalizeEnabled}
              onChange={onPersonalizeChange}
              authed={!!user}
            />
          </div>
        )}
        <FilterRowFlights
          key={`${flights[0]?.id ?? 'empty'}-${flights.length}`}
          flights={flights}
          onFiltersChange={setFilteredFlights}
          sortBy={sortBy}
          onSortChange={setSortBy}
        />
      </div>

      {/* Flight Results */}
      <div className="max-w-5xl mx-auto space-y-4">
        {filteredFlights.length === 0 && hasActiveFilters ? (
          <Card className="border-gray-200">
            <CardContent className="p-8">
              <div className="text-center text-gray-500">
                No flights match your filters. Try adjusting your filter criteria.
              </div>
            </CardContent>
          </Card>
        ) : (
          renderFlightList()
        )}

        {/* Pricing Legend */}
        <PricingLegend scope="flight" />
      </div>
    </div>
  );
}
